import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { FaChevronRight } from "react-icons/fa";
import { ACTIVITY_API_BASE, getCityImageUrl } from "../lib/activityApi";

const BASE_URL = ACTIVITY_API_BASE.replace("/api/activity", "");

export default function ActivitiesMegaMenu({ isOpen, onClose, onMouseEnter, anchorRef }) {
  const menuRef = useRef(null);
  const [countries, setCountries] = useState([]);
  const [countryCities, setCountryCities] = useState({}); // { countryId: [ { city, country } ] }
  const [hoveredCountryId, setHoveredCountryId] = useState(null);
  const [selectedCountryId, setSelectedCountryId] = useState(null); // click locks filter
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setHoveredCountryId(null);
    setSelectedCountryId(null);
    axios
      .get(`${ACTIVITY_API_BASE}/user/countries`)
      .then((res) => {
        const list = res.data?.data || [];
        setCountries(list);
        if (list.length === 0) {
          setCountryCities({});
          setLoading(false);
          return;
        }
        const topCountries = list.slice(0, 8);
        Promise.all(
          topCountries.map((country) =>
            axios.get(`${ACTIVITY_API_BASE}/user/cities/${country._id}`).then((r) => ({
              countryId: country._id,
              country,
              cities: (r.data?.data || []).slice(0, 6).map((city) => ({ city, country })),
            }))
          )
        )
          .then((results) => {
            const map = {};
            results.forEach(({ countryId, cities }) => {
              map[countryId] = cities;
            });
            setCountryCities(map);
          })
          .catch(() => setCountryCities({}))
          .finally(() => setLoading(false));
      })
      .catch(() => {
        setCountries([]);
        setCountryCities({});
        setLoading(false);
      });
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        isOpen &&
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        anchorRef.current &&
        !anchorRef.current.contains(e.target)
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose, anchorRef]);

  const handleLinkClick = () => onClose();

  // Right side: selected (click) takes priority, then hover, then all
  const allCityList = Object.values(countryCities).flat();
  const activeCountryId = selectedCountryId || hoveredCountryId;
  const displayList =
    activeCountryId && countryCities[activeCountryId]
      ? countryCities[activeCountryId]
      : allCityList;
  const displayCountry = activeCountryId ? countries.find((c) => c._id === activeCountryId) : null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={menuRef}
          onMouseEnter={onMouseEnter}
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
          className="absolute left-0 right-0 top-full mt-0 z-[100] bg-white shadow-xl border-b border-gray-200"
          style={{ minHeight: "320px" }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex gap-8">
              {/* Left sidebar – Best Cities + Best Activities in [Country]; hover filters right side */}
              <div className="w-56 shrink-0 border-r border-gray-100 pr-6">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedCountryId(null);
                    setHoveredCountryId(null);
                  }}
                  className="w-full text-left flex items-center justify-between py-2.5 text-gray-700 hover:text-blue-600 font-medium transition-colors group"
                >
                  Best Cities to Visit
                  <FaChevronRight className="w-3.5 h-3.5 text-gray-400 group-hover:text-blue-600 transition-all" />
                </button>
                {countries.map((country) => {
                  const isActive = selectedCountryId === country._id || hoveredCountryId === country._id;
                  return (
                    <button
                      key={country._id}
                      type="button"
                      onMouseEnter={() => setHoveredCountryId(country._id)}
                      onMouseLeave={() => setHoveredCountryId(null)}
                      onClick={() => setSelectedCountryId(selectedCountryId === country._id ? null : country._id)}
                      className={`w-full text-left py-2.5 font-medium transition-colors cursor-pointer flex items-center justify-between group ${
                        isActive ? "text-blue-600" : "text-gray-700 hover:text-blue-600"
                      } ${selectedCountryId === country._id ? "bg-blue-50/50 rounded-md -mx-1 px-1" : ""}`}
                    >
                      <span>Best Activities in {country.name}</span>
                      <FaChevronRight className="w-3.5 h-3.5 text-gray-400 group-hover:text-blue-600 shrink-0" />
                    </button>
                  );
                })}
                {!loading && countries.length === 0 && (
                  <p className="text-sm text-gray-500 py-2">No destinations available.</p>
                )}
              </div>

              {/* Right – city cards; filtered by hovered country. Click → check availability */}
              <div className="flex-1 overflow-hidden">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                  {displayCountry
                    ? `Activities in ${displayCountry.name}`
                    : "Popular Destinations"}
                </h3>
                {loading ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <div key={i} className="animate-pulse rounded-xl bg-gray-100 h-20" />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[280px] overflow-y-auto pr-2">
                    {displayList.map(({ city, country }) => (
                      <Link
                        key={city._id}
                        to={`/activities/${country._id}/${city._id}`}
                        onClick={handleLinkClick}
                        className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-blue-200 hover:bg-blue-50/50 transition-all group"
                      >
                        <div className="w-12 h-12 shrink-0 rounded-lg overflow-hidden bg-gray-100">
                          <img
                            src={getCityImageUrl(city.image, BASE_URL)}
                            alt={city.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="min-w-0">
                          <div className="font-bold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                            {city.name}
                          </div>
                          <div className="text-sm text-gray-500 truncate">{country.name}</div>
                        </div>
                        <FaChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0 ml-auto group-hover:text-blue-600" />
                      </Link>
                    ))}
                  </div>
                )}
                {!loading && displayList.length === 0 && (
                  <p className="text-sm text-gray-500 py-2">Select a country to see cities.</p>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
